import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { brl, nfmt } from "@/lib/format";
import { toast } from "sonner";
import { UploadCloud, FileText, Loader2, Trash2, Plus } from "lucide-react";

const emptyItem = {
  part_name: "",
  quantity: 1,
  cut_length_m: 0,
  holes_count: 0,
  sheet_id: "",
  price_per_meter_cut: 25,
  price_per_hole: 2.5,
  sheet_area_m2: 0,
  part_width_mm: 0,
  part_height_mm: 0,
  dxf_filename: "",
  image_b64: "",
  process: "Corte Plasma",
};

const PROCESSES = ["Corte Plasma", "Corte Laser", "Oxicorte"];
const METHODS = ["Pix", "Dinheiro", "Transferência", "Boleto"];

export default function QuoteNew() {
  const nav = useNavigate();
  const [clients, setClients] = useState([]);
  const [sheets, setSheets] = useState([]);
  const [clientId, setClientId] = useState("");
  const [items, setItems] = useState([{ ...emptyItem }]);
  const [setupFee, setSetupFee] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [tax, setTax] = useState(0);
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("Rascunho");
  const [process, setProcess] = useState("Corte Plasma");
  const [paymentMethods, setPaymentMethods] = useState(["Pix", "Transferência"]);
  const [saving, setSaving] = useState(false);
  const [analyzingIdx, setAnalyzingIdx] = useState(-1);

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data));
    api.get("/sheets").then((r) => setSheets(r.data));
  }, []);

  const updateItem = (idx, patch) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  const removeItem = (idx) =>
    setItems((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== idx)));

  const analyzeDxf = async (idx, file) => {
    if (!file) return;
    setAnalyzingIdx(idx);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/dxf/analyze", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      updateItem(idx, {
        cut_length_m: data.cut_length_m,
        holes_count: data.holes_count,
        sheet_area_m2: data.area_m2,
        part_width_mm: data.part_width_mm || 0,
        part_height_mm: data.part_height_mm || 0,
        image_b64: data.image_b64 || "",
        dxf_filename: data.filename,
        part_name: items[idx].part_name || data.filename.replace(/\.dxf$/i, ""),
      });
      toast.success(
        `DXF: ${nfmt(data.cut_length_m)} m · ${data.holes_count} furos`,
      );
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAnalyzingIdx(-1);
    }
  };

  // Totals
  const lineTotal = (it) =>
    (Number(it.quantity) || 1) *
    (Number(it.cut_length_m || 0) * Number(it.price_per_meter_cut || 0) +
      Number(it.holes_count || 0) * Number(it.price_per_hole || 0));

  const subtotal = items.reduce((s, it) => s + lineTotal(it), 0) + Number(setupFee || 0);
  const discountValue = subtotal * (Number(discount || 0) / 100);
  const afterDisc = subtotal - discountValue;
  const taxValue = afterDisc * (Number(tax || 0) / 100);
  const total = afterDisc + taxValue;
  const totalCutM = items.reduce((s, it) => s + Number(it.quantity || 1) * Number(it.cut_length_m || 0), 0);
  const totalHoles = items.reduce((s, it) => s + Number(it.quantity || 1) * Number(it.holes_count || 0), 0);

  const submit = async () => {
    if (!clientId) return toast.error("Selecione um cliente");
    if (items.some((it) => !it.part_name)) return toast.error("Informe o nome de cada peça");
    setSaving(true);
    try {
      const { data } = await api.post("/quotes", {
        client_id: clientId,
        items,
        setup_fee: Number(setupFee || 0),
        discount_percent: Number(discount || 0),
        tax_percent: Number(tax || 0),
        notes,
        status,
        process,
        payment_methods: paymentMethods,
      });
      toast.success("Orçamento criado " + data.number);
      nav(`/orcamentos/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <header>
        <div className="text-[11px] mono uppercase tracking-[0.3em] text-orange-400/80">
          novo · orçamento
        </div>
        <h1 className="display text-3xl font-bold mt-1">Gerar Orçamento</h1>
      </header>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <div className="card-tactical rounded-lg p-5">
            <label className="text-xs uppercase tracking-wider text-slate-400">Cliente</label>
            <select
              data-testid="quote-client-select"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              className="mt-1 w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm"
            >
              <option value="">— selecione —</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <label className="text-xs uppercase text-slate-400">Processo de corte</label>
                <select
                  value={process}
                  onChange={(e) => setProcess(e.target.value)}
                  className="mt-1 w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm"
                  data-testid="quote-process-select"
                >
                  {PROCESSES.map((p) => <option key={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase text-slate-400">Métodos de pagamento</label>
                <div className="mt-1 flex flex-wrap gap-2">
                  {METHODS.map((m) => {
                    const on = paymentMethods.includes(m);
                    return (
                      <button
                        type="button"
                        key={m}
                        onClick={() =>
                          setPaymentMethods((prev) =>
                            prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m],
                          )
                        }
                        className={`text-xs px-2.5 py-1.5 rounded border transition ${
                          on
                            ? "bg-orange-500/20 text-orange-200 border-orange-500/60"
                            : "bg-slate-900 text-slate-400 border-slate-700 hover:border-slate-500"
                        }`}
                        data-testid={`payment-${m}`}
                      >
                        {on ? "☑" : "☐"} {m}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {items.map((it, idx) => (
            <ItemCard
              key={idx}
              idx={idx}
              item={it}
              sheets={sheets}
              analyzing={analyzingIdx === idx}
              onChange={(patch) => updateItem(idx, patch)}
              onRemove={() => removeItem(idx)}
              onDxf={(file) => analyzeDxf(idx, file)}
              lineTotal={lineTotal(it)}
            />
          ))}

          <button
            onClick={() => setItems([...items, { ...emptyItem }])}
            className="w-full border-2 border-dashed border-slate-700 hover:border-orange-500 hover:text-orange-300 rounded-lg py-3 text-sm text-slate-400 transition flex items-center justify-center gap-2"
            data-testid="quote-add-item"
          >
            <Plus className="w-4 h-4" /> Adicionar peça
          </button>

          <div className="card-tactical rounded-lg p-5">
            <label className="text-xs uppercase tracking-wider text-slate-400">Observações</label>
            <textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-1 w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm"
              placeholder="Prazo, forma de pagamento, etc."
            />
          </div>
        </div>

        {/* Sidebar totals */}
        <div className="space-y-4">
          <div className="card-tactical rounded-lg p-5 sticky top-4">
            <div className="text-[11px] mono uppercase tracking-widest text-orange-400/80">
              cálculo · em tempo real
            </div>
            <div className="mt-3 space-y-2 text-sm">
              <Row label="Corte total" value={`${nfmt(totalCutM)} m`} />
              <Row label="Furos totais" value={totalHoles} />
              <div className="border-t border-slate-800 my-2" />

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[11px] uppercase text-slate-400">Setup R$</label>
                  <input
                    type="number"
                    step="0.01"
                    value={setupFee}
                    onChange={(e) => setSetupFee(e.target.value)}
                    className="mt-1 w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm mono"
                    data-testid="quote-setup-fee-input"
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase text-slate-400">Status</label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="mt-1 w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm"
                    data-testid="quote-status-select"
                  >
                    <option>Rascunho</option>
                    <option>Enviado</option>
                    <option>Aprovado</option>
                    <option>Rejeitado</option>
                    <option>Em Produção</option>
                  </select>
                </div>
                <div>
                  <label className="text-[11px] uppercase text-slate-400">Desconto %</label>
                  <input
                    type="number"
                    step="0.01"
                    value={discount}
                    onChange={(e) => setDiscount(e.target.value)}
                    className="mt-1 w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm mono"
                    data-testid="quote-discount-input"
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase text-slate-400">Impostos %</label>
                  <input
                    type="number"
                    step="0.01"
                    value={tax}
                    onChange={(e) => setTax(e.target.value)}
                    className="mt-1 w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm mono"
                    data-testid="quote-tax-input"
                  />
                </div>
              </div>

              <div className="border-t border-slate-800 my-2" />
              <Row label="Subtotal" value={brl(subtotal)} />
              <Row label="Desconto" value={"- " + brl(discountValue)} />
              <Row label="Impostos" value={brl(taxValue)} />
              <div className="flex justify-between items-baseline mt-3 pt-3 border-t border-slate-800">
                <span className="text-sm uppercase tracking-widest text-orange-400">Total</span>
                <span className="display text-3xl font-bold text-orange-300 mono">
                  {brl(total)}
                </span>
              </div>

              <button
                onClick={submit}
                disabled={saving}
                data-testid="quote-generate-submit-button"
                className="mt-4 w-full pill-accent rounded-md py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                Gerar orçamento
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between items-baseline">
      <span className="text-xs text-slate-400 uppercase tracking-wider">{label}</span>
      <span className="mono text-slate-200">{value}</span>
    </div>
  );
}

function ItemCard({ idx, item, sheets, analyzing, onChange, onRemove, onDxf, lineTotal }) {
  const inputRef = useRef();
  const [drag, setDrag] = useState(false);

  return (
    <div className="card-tactical rounded-lg p-5">
      <div className="flex justify-between items-center mb-3">
        <div className="text-[11px] mono uppercase tracking-widest text-orange-400/80">
          peça · {String(idx + 1).padStart(2, "0")}
        </div>
        <button
          onClick={onRemove}
          className="text-slate-400 hover:text-red-400 text-xs flex items-center gap-1"
          data-testid={`quote-remove-item-${idx}`}
        >
          <Trash2 className="w-3.5 h-3.5" /> remover
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* DXF drop zone */}
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDrag(true);
          }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDrag(false);
            const f = e.dataTransfer.files?.[0];
            if (f) onDxf(f);
          }}
          onClick={() => inputRef.current?.click()}
          className={`relative overflow-hidden cursor-pointer border-2 border-dashed rounded-lg p-4 text-center transition min-h-[160px] flex flex-col items-center justify-center ${
            drag
              ? "border-orange-400 bg-orange-500/5"
              : "border-slate-700 hover:border-orange-500 hover:bg-slate-800/40"
          } ${analyzing ? "scan-line" : ""}`}
          data-testid={`dxf-file-dropzone-${idx}`}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".dxf"
            className="hidden"
            onChange={(e) => onDxf(e.target.files?.[0])}
            data-testid={`dxf-upload-input-${idx}`}
          />
          {analyzing ? (
            <>
              <Loader2 className="w-6 h-6 text-orange-400 animate-spin" />
              <div className="text-xs mono text-orange-300 mt-2">analisando dxf…</div>
            </>
          ) : item.dxf_filename ? (
            <>
              <FileText className="w-8 h-8 text-orange-400" />
              <div className="mt-2 mono text-sm text-orange-200 truncate max-w-full px-2">
                {item.dxf_filename}
              </div>
              <div className="mt-1 text-[11px] text-slate-400">
                {nfmt(item.cut_length_m)} m · {item.holes_count} furos
              </div>
              <div className="mt-2 text-[10px] mono uppercase tracking-widest text-slate-500">
                clique para trocar
              </div>
            </>
          ) : (
            <>
              <UploadCloud className="w-8 h-8 text-slate-500" />
              <div className="mt-2 text-sm text-slate-300">
                Solte um arquivo <span className="mono text-orange-300">.DXF</span>
              </div>
              <div className="text-[11px] text-slate-500">ou clique para selecionar</div>
            </>
          )}
        </div>

        {/* Fields */}
        <div className="space-y-2">
          <div>
            <label className="text-[11px] uppercase text-slate-400">Nome da peça</label>
            <input
              value={item.part_name}
              onChange={(e) => onChange({ part_name: e.target.value })}
              className="mt-1 w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm"
              data-testid={`quote-part-name-${idx}`}
            />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-[11px] uppercase text-slate-400">Qtd</label>
              <input
                type="number"
                min={1}
                value={item.quantity}
                onChange={(e) => onChange({ quantity: parseInt(e.target.value) || 1 })}
                className="mt-1 w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm mono"
                data-testid={`quote-quantity-${idx}`}
              />
            </div>
            <div>
              <label className="text-[11px] uppercase text-slate-400">Corte (m)</label>
              <input
                type="number"
                step="0.001"
                value={item.cut_length_m}
                onChange={(e) => onChange({ cut_length_m: parseFloat(e.target.value) || 0 })}
                className="mt-1 w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm mono"
                data-testid={`quote-cut-length-input-${idx}`}
              />
            </div>
            <div>
              <label className="text-[11px] uppercase text-slate-400">Furos</label>
              <input
                type="number"
                value={item.holes_count}
                onChange={(e) => onChange({ holes_count: parseInt(e.target.value) || 0 })}
                className="mt-1 w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm mono"
                data-testid={`quote-holes-count-input-${idx}`}
              />
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase text-slate-400">Chapa</label>
            <select
              value={item.sheet_id}
              onChange={(e) => onChange({ sheet_id: e.target.value })}
              className="mt-1 w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm"
              data-testid={`quote-sheet-select-${idx}`}
            >
              <option value="">— selecione —</option>
              {sheets.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.material} · {s.thickness_mm}mm · {brl(s.price_per_m2)}/m²
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] uppercase text-slate-400">R$ / m corte</label>
              <input
                type="number"
                step="0.01"
                value={item.price_per_meter_cut}
                onChange={(e) => onChange({ price_per_meter_cut: parseFloat(e.target.value) || 0 })}
                className="mt-1 w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm mono"
                data-testid={`quote-price-per-meter-input-${idx}`}
              />
            </div>
            <div>
              <label className="text-[11px] uppercase text-slate-400">R$ / furo</label>
              <input
                type="number"
                step="0.01"
                value={item.price_per_hole}
                onChange={(e) => onChange({ price_per_hole: parseFloat(e.target.value) || 0 })}
                className="mt-1 w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm mono"
                data-testid={`quote-price-per-hole-input-${idx}`}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="mt-3 pt-3 border-t border-slate-800 flex justify-between items-baseline">
        <span className="text-[11px] uppercase tracking-widest text-slate-400">Subtotal da peça</span>
        <span className="mono text-orange-300 text-lg">{brl(lineTotal)}</span>
      </div>
    </div>
  );
}
